import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import "./biography.css";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { buildHeaders } from "../../api/buildHeaders";
import {
  parsePhoneNumberFromString,
  getExampleNumber,
} from "libphonenumber-js";
import metadata from "libphonenumber-js/metadata.full.json";
import { REST_API_URL } from "../../api/config";
import { LanguageSwitcher } from "../../components/language/languageSwitcher/LanguageSwitcher";
import { LocationSelector } from "../../components/LocationSelector";
import { PhoneField } from "../../components/PhoneField/PhoneField";
import { extractErrorMessage } from "../../utils/extractErrorMessage";
import { getWorkplacesThunk } from "../../redux/slices/authSlice/asyncthunk";
import { useAppDispatch, useAppSelector } from "../../hooks/redux/useRedux";
import { selectWorkplacesList } from "../../redux/slices/authSlice/AuthSlice";
const BASE_URL = REST_API_URL;

export default function Biography() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [currency, setCurrency] = useState("");
  const [currencies, setCurrencies] = useState([]);
  const [openCurrency, setOpenCurrency] = useState(false);
 
  const [selectedWorkplacesIds, setSelectedWorkplacesIds] = useState([]);
  const workplacesList = useAppSelector(selectWorkplacesList);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingCurrency, setLoadingCurrency] = useState(true);
  const selectedCountryRef = useRef("US");
  const [position, setPosition] = useState(null);
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [addressDetails, setAddressDetails] = useState({
    country: "",
    city: "",
    address: "",
    state: "",
    postcode: "",
    fullAddress: "",
  });
  const navigate = useNavigate();
  const dropdownRef = useRef(null);


  const token = useMemo(
    () =>
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken") ||
      "",
    [],
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenCurrency(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    dispatch(getWorkplacesThunk()); 
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setPosition({ lat: 40.7128, lng: -74.006 }),
      );
    } else {
      setPosition({ lat: 40.7128, lng: -74.006 });
    }
  }, []);

  useEffect(() => {
    if (addressDetails.fullAddress) {
      setAddress(addressDetails.fullAddress);
    }
    if (addressDetails.city) {
      setCity(addressDetails.city);
    }
  }, [addressDetails]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      setLoadingCurrency(true);
      try {
        if (!token) throw new Error("Missing token");
        const res = await fetch(`${BASE_URL}/service/currencies/`, {
          headers: buildHeaders(),
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        setCurrencies(
          data.map((item) => ({
            code: item.short_name,
            name: item.short_name,
            flag: item.icon || "",
          })),
        );
        setCurrency(data[0]?.short_name || "");
      } catch (err) {
        setCurrencies([
          { code: "USD", name: "USD ($)", flag: "https://flagcdn.com/us.svg" },
        ]);
        setCurrency("USD");
      } finally {
        setLoadingCurrency(false);
      }
    };
    fetchCurrencies();
  }, [token]);

  const handlePhoneChange = (value) => {
    const cleaned = value.replace(/[^\d+\-\s]/g, "");
    setPhone(cleaned);
    const country = selectedCountryRef.current;
    const parsed = parsePhoneNumberFromString(cleaned, country);
    if (!parsed) {
      setErrors((prev) => ({ ...prev, phone: t("auth.errorPhoneInvalid") }));
      return;
    }
    const numLength = parsed.nationalNumber.length;
    const lengthInfo = getExampleNumber(country, metadata)?.nationalNumber
      .length;

    if (
      lengthInfo &&
      (numLength < lengthInfo - 1 || numLength > lengthInfo + 1)
    ) {
      setErrors((prev) => ({ ...prev, phone: t("auth.errorPhoneLength") }));
      return;
    }

    if (!parsed.isValid()) {
      setErrors((prev) => ({ ...prev, phone: t("auth.errorPhoneFormat") }));
      return;
    }
    setErrors((prev) => {
      const newErr = { ...prev };
      delete newErr.phone;
      return newErr;
    });
  };

  const handleFullNameChange = (e) => {
    const value = e.target.value;
    const cleaned = value
      .replace(/[^a-zA-ZÀ-ÖØ-öø-ÿА-яЁёÀ-ÿÄäÖöÜüß\u0400-\u04FF\s-']/g, "")
      .replace(/\s+/g, " ")
      .replace(/^\s+/, "");
    setFullName(cleaned);

    if (errors.fullName) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.fullName;
        return next;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!fullName.trim() || fullName.length < 2)
      newErrors.fullName = t("auth.errorFullName");
    const parsed = parsePhoneNumberFromString(
      phone.replace(/[\s-]/g, ""),
      selectedCountryRef.current,
    );
    if (!parsed || !parsed.isValid())
      newErrors.phone = t("auth.errorPhoneInvalid");
    if (!currency) newErrors.currency = t("auth.errorCurrency");
    if (selectedWorkplacesIds.length === 0)
      newErrors.work = t("auth.errorWork");
    if (!position) newErrors.location = t("auth.errorLocation");

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error(t("auth.errorFixErrors"));
      return false;
    }
    return true;
  };

  const handleNext = async () => {
    if (!validateForm()) return;
    setLoading(true);
    const sanitizedPhone = phone.replace(/[^\d+]/g, "");
    const parsed = parsePhoneNumberFromString(
      sanitizedPhone,
      selectedCountryRef.current,
    );

    const body = {
      full_name: fullName,
      phone_number: parsed?.number || sanitizedPhone,
      address_url: `https://www.google.com/maps/search/?api=1&query=${position?.lat},${position?.lng}`,
      currency,
      work_place_ids: selectedWorkplacesIds,
    };

    try {
      const res = await fetch(`${BASE_URL}/auth/userinfo/`, {
        method: "POST",
        headers: buildHeaders(),
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error(extractErrorMessage(await res.json()));

      toast.success(t("auth.successSaved"));
      localStorage.setItem("userInfoCompleted", "true");
      navigate("/choose-niche", { replace: true });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleWorkplaceChange = (workplaceId) => {
    setSelectedWorkplacesIds((prev) =>
      prev.includes(workplaceId)
        ? prev.filter((id) => id !== workplaceId)
        : [...prev, workplaceId],
    );
    
  };

  return (
    <motion.div
      className="biography"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <main className="biography-main">
        <motion.div
          className="biography-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="biography-header">
            <div className="biography-text">
              <h2>{t("auth.biographyHeader")}</h2>
              <p>{t("auth.biographySubheader")}</p>
            </div>
          </div>

          <div className="biography-content">
            <div className="biography-form" ref={dropdownRef}>
              <LanguageSwitcher />
              <div className="biography-form-group">
                <label>
                  {t("auth.fullNameLabel")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder={t("auth.fullNamePlaceholder")}
                  value={fullName}
                  onChange={handleFullNameChange}
                />
                {errors.fullName && (
                  <span className="input-error-text">{errors.fullName}</span>
                )}
              </div>

              <PhoneField
                phone={phone}
                error={errors.phone}
                onPhoneChange={handlePhoneChange}
                onCountryChange={(c) => {
                  selectedCountryRef.current = c;
                  setOpenCurrency(false);
                }}
              />

              <div className="biography-form-group biography-form-group-currency">
                <label>
                  {t("auth.currencyLabel")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <div
                  className={`currency-field ${errors.currency ? "invalid" : ""}`}
                  onClick={() => {
                    if (!loadingCurrency) {
                      setOpenCurrency(!openCurrency);
                    }
                  }}
                >
                  <span>
                    {loadingCurrency
                      ? t("auth.loading")
                      : currency || t("auth.selectCurrency")}
                  </span>
                </div>
                {openCurrency && (
                  <div className="currency-menu">
                    {currencies.map((item) => (
                      <div
                        key={item.code}
                        className="currency-item"
                        onClick={() => {
                          setCurrency(item.code);
                          setOpenCurrency(false);
                        }}
                      >
                        <span>{item.name}</span>
                      </div>
                    ))}
                  </div>
                )}
                {errors.currency && (
                  <span className="input-error-text">{errors.currency}</span>
                )}
              </div>
            </div>

            <div className="flex flex-col w-full md:w-[50%] h-full">
              <div className="location-selector-wrapper">
                <LocationSelector
                  position={position}
                  setPosition={setPosition}
                  setAddressDetails={setAddressDetails}
                />
              </div>
              {errors.location && (
                <span className="input-error-text mt-2">{errors.location}</span>
              )}
            </div>
          </div>

          <div className="biography-work">
            <label className="biography-work-title">
              {t("auth.workTitle")} <span className="text-red-500">*</span>
            </label>
            <div
              className={`biography-checkbox-group ${errors.work ? "invalid" : ""}`}
            >
              {
                workplacesList.map((item) => (
                  <label key={item.id}>
                    <input
                      type="checkbox"
                      checked={selectedWorkplacesIds.includes(item.id)}
                      onChange={() => handleWorkplaceChange(item.id)}
                    />
                    <span>{item.place_title}</span>
                  </label>
                ))
              }
            </div>
            {errors.work && (
              <span className="input-error-text">{errors.work}</span>
            )}
          </div>

          <div className="biography-btn-footer">
            <motion.button
              className="biography-next-btn"
              onClick={handleNext}
              disabled={loading}
            >
              {loading ? t("auth.saving") : t("auth.nextButton")}
            </motion.button>
            <span className="biography-step">{t("step")} 1/6</span>
          </div>
        </motion.div>
      </main>
    </motion.div>
  );
}
